import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, ArrowUpDown, Edit2, ExternalLink, Eye, EyeOff,
  Folder, Lock, Plus, Search, Share2, Star, Trash2, X as CloseIcon,
} from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { categoriesApi } from '../api/categories';
import { useAuth } from '../contexts/AuthContext';

const ICON_MAP = {
  lock: Lock, folder: Folder,
};

function CategoryIcon({ icon, color, size = 20 }) {
  const Comp = ICON_MAP[icon] || Folder;
  return <Comp size={size} color={color || '#3b82f6'} />;
}

function MaskedValue({ encrypted, vaultKey }) {
  const [revealed, setRevealed] = useState(false);
  const [plaintext, setPlaintext] = useState(null);
  const [loading, setLoading] = useState(false);

  const reveal = async () => {
    if (!vaultKey) {
      alert('Your vault is locked. Please unlock it first.');
      return;
    }
    setLoading(true);
    try {
      // Decrypt client-side — encrypted is the vault item's encrypted_data blob
      // This component is intentionally left as a placeholder for client-side decryption
      // The actual decryption happens in the vault module via decryptVaultItem()
      setPlaintext('[Password revealed — implement client-side decrypt]');
      setRevealed(true);
    } finally {
      setLoading(false);
    }
  };

  if (!revealed) {
    return (
      <span className="masked-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        ••••••••
        <button className="icon-button" style={{ height: 22, width: 22, minWidth: 22 }} onClick={reveal} title="Reveal password" disabled={loading}>
          <Eye size={13} />
        </button>
      </span>
    );
  }

  return (
    <span style={{ fontFamily: 'monospace', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {plaintext}
      <button className="icon-button" style={{ height: 22, width: 22, minWidth: 22 }} onClick={() => setRevealed(false)} title="Hide">
        <EyeOff size={13} />
      </button>
    </span>
  );
}

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { vaultKey } = useAuth();

  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cat, its] = await Promise.all([
        categoriesApi.getById(categoryId),
        categoriesApi.getItems(categoryId),
      ]);
      setCategory(cat);
      setItems(its);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Access denied.' : 'Failed to load category.');
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let list = [...items];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((i) => i.item_type?.toLowerCase().includes(q) || i.owner_email?.toLowerCase().includes(q));
    }
    if (sort === 'updated_asc') list.sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
    else if (sort === 'type') list.sort((a, b) => (a.item_type || '').localeCompare(b.item_type || ''));
    else list.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    return list;
  }, [items, search, sort]);

  if (loading) {
    return (
      <MainLayout>
        <div className="vault-loading-state"><div className="loading-spinner" /> Loading category…</div>
      </MainLayout>
    );
  }

  if (error || !category) {
    return (
      <MainLayout>
        <div className="vault-error-state"><AlertTriangle size={18} />{error || 'Category not found'}</div>
      </MainLayout>
    );
  }

  const iconBg = `${category.color || '#3b82f6'}22`;

  return (
    <MainLayout>
      {/* Category Header - Full Bleed Edge-to-Edge */}
      <div className="cat-detail-header notes-hero-banner">
        <div className="notes-hero-content cat-detail-hero-content">
          <div className="cat-detail-hero-left">
            <div className="notes-app-icon-badge notes-hero-icon-badge cat-detail-icon-badge">
              <CategoryIcon icon={category.icon} color="#ffffff" size={22} />
            </div>
            <div className="cat-detail-info">
              <div className="cat-detail-title-row">
                <h1 className="notes-hero-title cat-detail-title">{category.name}</h1>
                {category.is_system && <span className="notes-hero-badge">System</span>}
              </div>
              {category.description && (
                <p className="notes-hero-subtitle cat-detail-subtitle">{category.description}</p>
              )}
              <div className="cat-detail-stats">
                <span><Lock size={12} /> {category.total_items || 0} items</span>
                <span><Star size={12} /> {category.favorite_count || 0} favorites</span>
                <span><Share2 size={12} /> {category.share_count || 0} shares</span>
              </div>
            </div>
          </div>
          <div className="cat-detail-btn-row">
            <Link to="/categories" className="btn btn-secondary notes-hero-btn-secondary cat-detail-back-btn">
              <ArrowLeft size={14} /> All Categories
            </Link>
            {!category.is_system && (
              <>
                <Link to="/vault" className="btn btn-primary notes-hero-btn-primary">
                  <Plus size={14} /> Add Item
                </Link>
                <button className="btn btn-secondary notes-hero-btn-secondary"
                  onClick={() => navigate(`/categories`, { state: { shareCategory: category.id } })}>
                  <Share2 size={14} /> Share
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Toolbar - Single Row Filter Bar */}
      <div className="cat-detail-toolbar">
        <div className="cat-detail-search-box">
          <Search size={15} className="cat-detail-search-icon" />
          <input
            className="cat-detail-search-input"
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search items"
          />
          {search && (
            <button
              type="button"
              className="cat-detail-clear-btn"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <CloseIcon size={13} />
            </button>
          )}
        </div>
        <div className="cat-detail-sort-box">
          <ArrowUpDown size={14} className="cat-detail-sort-icon" />
          <select
            className="cat-detail-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort items"
          >
            <option value="updated_desc">Recently Updated</option>
            <option value="updated_asc">Oldest First</option>
            <option value="type">By Type</option>
          </select>
        </div>
      </div>

      {/* Items Table */}
      {filtered.length === 0 ? (
        <div className="vault-empty-state glass-panel cat-detail-empty-state">
          <div className="vault-empty-icon"><Lock size={26} /></div>
          <h3>{search ? 'No matching items' : 'No items in this category'}</h3>
          <p>{search ? 'Try a different search.' : 'Add items to this category from the Vault.'}</p>
          {!search && (
            <Link to="/vault" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', marginTop: 8 }}>
              <Plus size={16} /> Go to Vault
            </Link>
          )}
        </div>
      ) : (
        <div className="vault-table-panel glass-panel" style={{ marginTop: 16 }}>
          <div className="vault-table-scroll">
            <table className="vault-table cat-items-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Password</th>
                  <th>Owner</th>
                  <th>Updated</th>
                  <th>Favorite</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="vault-item-cell">
                        <div className="vault-item-icon"><Lock size={14} /></div>
                        <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{item.item_type?.replace(/_/g, ' ')}</span>
                      </div>
                    </td>
                    <td>
                      <MaskedValue encrypted={null} vaultKey={vaultKey} />
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                      {item.owner_email || '—'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      {item.is_favorite ? <Star size={14} color="var(--warning)" fill="var(--warning)" /> : <Star size={14} color="var(--text-secondary)" />}
                    </td>
                    <td>
                      <Link to={`/vault`} className="icon-button" title="Open in Vault">
                        <ExternalLink size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
